const normalizeKeyValue = (value: string) => {
  const trimmed = value.trim();
  const unquoted =
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
      ? trimmed.slice(1, -1)
      : trimmed;

  return unquoted.replace(/\\n/g, "\n").trim();
};

const extractBase64Body = (keyValue: string) => {
  const normalized = normalizeKeyValue(keyValue);
  const beginMarker = "-----BEGIN PUBLIC KEY-----";
  const endMarker = "-----END PUBLIC KEY-----";

  // Preferred path: full PEM with header/footer (quoted multiline supported)
  const pemMatch = normalized.match(
    /-----BEGIN PUBLIC KEY-----([\s\S]*?)-----END PUBLIC KEY-----/
  );
  if (pemMatch?.[1]) {
    const body = pemMatch[1].replace(/\s/g, "");
    return body;
  }

  // Common failure mode: env value got truncated and only BEGIN marker is present
  if (normalized.includes(beginMarker) && !normalized.includes(endMarker)) {
    throw new Error(
      "Public key appears truncated (BEGIN marker found without END marker). If using .env multiline values, ensure the full quoted key is loaded after restarting frontend."
    );
  }

  // Fallback path: body-only key without header/footer
  const compact = normalized.replace(/\s/g, "");
  if (!compact) {
    throw new Error("Public key is empty");
  }

  const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
  if (!base64Regex.test(compact) || compact.length % 4 !== 0) {
    throw new Error(
      "Public key is not valid base64/PEM. Check REACT_APP_ENCRYPTION_PUBLIC_KEY formatting."
    );
  }
  return compact;
};

const pemToArrayBuffer = (publicKeyValue: string) => {
  const base64 = extractBase64Body(publicKeyValue);
  let binary = "";
  try {
    binary = window.atob(base64);
  } catch (error) {
    throw new Error(
      "Public key base64 decode failed. Verify REACT_APP_ENCRYPTION_PUBLIC_KEY content."
    );
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const bufferToBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const getPublicKey = async () => {
  if (!window?.crypto?.subtle) {
    throw new Error(
      "Browser crypto is unavailable. Use HTTPS/localhost and a modern browser."
    );
  }

  const publicKeyPem = process.env.REACT_APP_ENCRYPTION_PUBLIC_KEY;
  if (!publicKeyPem) {
    throw new Error(
      "Missing REACT_APP_ENCRYPTION_PUBLIC_KEY in frontend environment"
    );
  }

  return window.crypto.subtle.importKey(
    "spki",
    pemToArrayBuffer(publicKeyPem),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
};

export const encryptFieldWithPublicKey = async (value: string) => {
  if (!value || !value.trim()) {
    throw new Error("Encryption input value is missing");
  }

  const publicKey = await getPublicKey();
  const encoded = new TextEncoder().encode(value);
  const encrypted = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    encoded
  );
  return bufferToBase64(encrypted);
};
